import { DomainModelRole } from '@/constants/cardTypes'
import { functionType } from '@/composables/useWorkflowAssistantFunctions'
import { sanitizeCardDescription } from '@/shared/utils/sanitize'
import {
  resolveSchema,
  resolveDomainEvent,
  resolveDomainEventOrDecision,
  resolveLane
} from '@/shared/utils/workflowSpec'

/**
 * Main validation function
 * @param {string} functionName - The name of the function to execute
 * @param {object} args - The arguments passed to the function
 * @param {object} context - Additional context (workflow, svgGraph, etc.)
 * @returns {object} - { valid: boolean, error: string | null }
 */
export const validateWorkflowAssistantAction = (functionName, args, context) => {
  const validators = {
    [functionType.CREATE_CARD]: validateCreateCard,
    [functionType.CREATE_EVENT]: validateCreateEvent,
    [functionType.UPDATE_EVENT]: validateUpdateEvent,
    [functionType.DELETE_EVENT]: validateDeleteEvent,
    [functionType.DELETE_LANE]: validateDeleteLane,
    [functionType.CREATE_ENTITY]: validateCreateEntities,
    [functionType.DELETE_ENTITY]: validateDeleteEntity,
    [functionType.UPDATE_ENTITY]: validateUpdateEntities,
    [functionType.UPDATE_CARD]: validateUpdateCard,
    [functionType.CREATE_COMMAND]: validateCreateCommands,
    [functionType.UPDATE_COMMAND]: validateUpdateCommands,
    [functionType.CREATE_READ_MODEL]: validateCreateReadModels,
    [functionType.UPDATE_READ_MODEL]: validateUpdateReadModels,
    [functionType.CREATE_DOMAIN_EVENT_SCHEMA]: validateCreateDomainEventSchemas,
    [functionType.UPDATE_DOMAIN_EVENT_SCHEMA]: validateUpdateDomainEventSchemas
  }

  const validator = validators[functionName]

  if (!validator) {
    // No specific validation needed for this function
    return { valid: true, error: null }
  }

  return validator(args, context)
}

/**
 * Validates creating a card
 * Prevents adding multiple command or aggregate root cards to the same event
 */
const validateCreateCard = (args, context) => {
  const { domainEvent: eventRef, cardTypeId } = args
  const { workflow, cardTypes } = context

  if (!eventRef || !cardTypeId) {
    return {
      valid: false,
      error: 'Missing required information: domain event or card type ID.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const cardType = (cardTypes || []).find((ct) => ct.id === cardTypeId)

  if (!cardType) {
    return {
      valid: false,
      error: 'Card type not found. Please specify a valid card type.'
    }
  }

  const isCommandOrAggregate =
    [DomainModelRole.COMMAND, DomainModelRole.AGGREGATE_ROOT].includes(cardType.domainModelRole)

  if (!isCommandOrAggregate) {
    return { valid: true, error: null }
  }

  const existingCards = event.cards || []

  const sameRoleCardTypeIds = (cardTypes || [])
    .filter((ct) => ct.domainModelRole === cardType.domainModelRole)
    .map((ct) => ct.id)

  const hasExistingCardOfSameRole = existingCards.some((card) =>
    sameRoleCardTypeIds.includes(card.cardTypeId)
  )

  if (hasExistingCardOfSameRole) {
    const cardTypeName = cardType.domainModelRole === DomainModelRole.COMMAND ? 'Command' : 'Aggregate Root'
    return {
      valid: false,
      error: `Cannot add ${cardTypeName} card. The event "${event.description}" already has a ${cardTypeName} card. Each event can only have one ${cardTypeName} card.`
    }
  }

  return { valid: true, error: null }
}

const validateUpdateCard = (args, context) => {
  const { domainEvent: eventRef, currentDescription, schema: schemaRef } = args
  const { workflow, cardTypes } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event is required.'
    }
  }

  if (!currentDescription) {
    return {
      valid: false,
      error: 'Current card description is required to identify the card.'
    }
  }

  const event = resolveDomainEvent(eventRef, workflow)

  if (!event) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const sanitized = sanitizeCardDescription(currentDescription)
  const card = (event.cards || []).find((c) =>
    sanitizeCardDescription(c.description || '') === sanitized
  )

  if (!card) {
    return {
      valid: false,
      error: `Card "${currentDescription}" not found on event "${event.description}".`
    }
  }

  if (schemaRef !== undefined && schemaRef) {
    const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))
    const cardType = cardTypesMap[card.cardTypeId]

    const allowedRoles = [
      DomainModelRole.COMMAND,
      DomainModelRole.AGGREGATE_ROOT,
      DomainModelRole.READ_MODEL
    ]

    if (cardType && !allowedRoles.includes(cardType.domainModelRole)) {
      return {
        valid: false,
        error: `Cannot assign schema to this card. Schema can only be assigned to cards with domain model role: Command, AggregateRoot, or ReadModel. This card has role: ${cardType.domainModelRole || 'none'}.`
      }
    }

    const schema = resolveSchema(schemaRef, workflow, cardTypesMap)

    if (!schema) {
      return {
        valid: false,
        error: `Schema "${refDisplayName(schemaRef)}" not found.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating an event
 */
const validateCreateEvent = (args, context) => {
  const { description, follows } = args
  const { workflow } = context

  if (typeof description !== 'string' || description.trim() === '') {
    return {
      valid: false,
      error: 'Event description is required. Please provide a name for the event.'
    }
  }

  if (description.length > 40) {
    return {
      valid: false,
      error: `Event description is too long (${description.length} characters). Maximum length is 40 characters. Please use a shorter description.`
    }
  }

  if (follows && follows !== 'start') {
    const parentEvent = resolveDomainEventOrDecision(follows, workflow)

    if (!parentEvent) {
      return {
        valid: false,
        error: `Parent event "${refDisplayName(follows)}" not found. Please specify an existing event or use "start" to create a new starting point.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating an event
 */
const validateUpdateEvent = (args, context) => {
  const { domainEvent: eventRef, description } = args
  const { workflow } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event reference is required to update an event.'
    }
  }

  const resolvedEvent = resolveDomainEventOrDecision(eventRef, workflow)

  if (!resolvedEvent) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  if (description !== undefined) {
    if (typeof description !== 'string') {
      return {
        valid: false,
        error: 'Event description must be text.'
      }
    }

    if (description.trim() === '') {
      return {
        valid: false,
        error: 'Event description cannot be empty.'
      }
    }

    if (description.length > 40) {
      return {
        valid: false,
        error: `Event description is too long (${description.length} characters). Maximum length is 40 characters. Please use a shorter description.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting an event
 */
const validateDeleteEvent = (args, context) => {
  const { domainEvent: eventRef } = args
  const { workflow, svgGraph } = context

  if (!eventRef) {
    return {
      valid: false,
      error: 'Domain event reference is required to delete an event.'
    }
  }

  const resolvedEvent = resolveDomainEventOrDecision(eventRef, workflow)

  if (!resolvedEvent) {
    return {
      valid: false,
      error: `Event "${refDisplayName(eventRef)}" not found.`
    }
  }

  const eventId = resolvedEvent.id
  const svgNode = svgGraph[eventId]

  if (!svgNode) {
    return {
      valid: false,
      error: 'Event not found in the diagram.'
    }
  }

  // Check if this is a decision with multiple children
  if (svgNode.type === 'bpmn:Gateway') {
    const children = Object.values(svgGraph).filter((node) =>
      node.parents && node.parents.includes(eventId)
    )

    if (children.length > 1) {
      return {
        valid: false,
        error: `Cannot delete decision "${svgNode.name}". This decision has multiple child events (${children.length}). Please reduce the number of children to one before deleting, or delete the child events first.`
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting a lane
 */
const validateDeleteLane = (args, context) => {
  const { lane: laneName } = args
  const { workflow, svgGraph } = context

  if (!laneName) {
    return {
      valid: false,
      error: 'Lane name is required to delete a lane.'
    }
  }

  const resolvedLane = resolveLane(laneName, workflow)

  if (!resolvedLane) {
    return {
      valid: false,
      error: `Lane "${laneName}" not found. Available lanes: ${(workflow.lanes || []).map((l) => `"${l.name}"`).join(', ')}.`
    }
  }

  // Check if lane has any events
  const hasEvents = Object.values(svgGraph).some((node) => node.laneId === resolvedLane.id)

  if (hasEvents) {
    return {
      valid: false,
      error: `Cannot delete lane "${laneName}". This lane contains events. Please move or delete all events from this lane before deleting it.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates deleting an entity
 */
const validateDeleteEntity = (args, context) => {
  const { entity: entityRef } = args
  const { workflow } = context

  if (!entityRef) {
    return {
      valid: false,
      error: 'Entity reference is required to delete an entity.'
    }
  }

  const entity = resolveSchema(entityRef, workflow)

  if (!entity) {
    return {
      valid: false,
      error: `Entity "${refDisplayName(entityRef)}" not found.`
    }
  }

  // Check if entity is referenced by other entities
  const entityId = entity.id
  const referencedBy = [];

  (workflow.schemas || []).forEach((otherEntity) => {
    if (otherEntity.id === entityId) {
      return
    }

    const hasReference = (otherEntity.fields || []).some((field) => field.relatedEntityId === entityId)

    if (hasReference) {
      referencedBy.push(otherEntity.name)
    }
  })

  if (referencedBy.length > 0) {
    return {
      valid: false,
      error: `Cannot delete entity "${entity.name}". It is referenced by other entities: ${referencedBy.join(', ')}. Please remove these references first.`
    }
  }

  // Check if entity is used by aggregate-root cards, read models, or schema fields on events.
  // Collect the specific blockers (not just event names) so the message tells the caller what to remove.
  const usedBy = [];

  (workflow.domainEvents || []).forEach((event) => {
    (event.cards || []).forEach((card) => {
      // AggregateRoot card that points directly at this entity
      if (card.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT && card.schemaId === entityId) {
        usedBy.push(`aggregate root on "${event.description}"`)
        return
      }

      // ReadModel card whose query schema queries this entity
      if (card.cardType?.domainModelRole === DomainModelRole.READ_MODEL && card.schemaId) {
        const querySchema = (workflow.schemas || []).find((schema) => schema.id === card.schemaId)
        if (querySchema?.entityId === entityId) {
          usedBy.push(`read model "${querySchema.name}" on "${event.description}"`)
          return
        }
      }

      // Any card whose linked schema has a field referencing this entity
      if (card.schemaId) {
        const schema = (workflow.schemas || []).find((item) => item.id === card.schemaId)
        const referencingField = (schema?.fields || []).find((field) => field.relatedEntityId === entityId)
        if (referencingField) {
          usedBy.push(`the "${referencingField.name}" field of "${schema.name}" on "${event.description}"`)
        }
      }
    })
  })

  if (usedBy.length > 0) {
    return {
      valid: false,
      error: `Cannot delete entity "${entity.name}". It is still referenced by: ${[...new Set(usedBy)].join('; ')}. Remove or reassign those (delete the read models / aggregate-root cards, or clear the referencing fields) before deleting the entity.`
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating entities (bulk)
 */
const validateCreateEntities = (args, context) => {
  const { entities } = args
  const { workflow } = context

  if (!Array.isArray(entities) || entities.length === 0) {
    return {
      valid: false,
      error: 'At least one entity is required.'
    }
  }

  for (const entity of entities) {
    const { name, fields } = entity || {}

    if (!name || name.trim() === '') {
      return {
        valid: false,
        error: 'Each entity must have a name.'
      }
    }

    if (fields && Array.isArray(fields)) {
      for (const field of fields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: `All fields must have a name (entity "${name}").`
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating entities (bulk)
 */
const validateUpdateEntities = (args, context) => {
  const { entities } = args
  const { workflow } = context

  if (!Array.isArray(entities) || entities.length === 0) {
    return {
      valid: false,
      error: 'At least one entity update is required.'
    }
  }

  for (const update of entities) {
    const { entity: entityRef, name, addFields, updateFields, removeFields } = update || {}

    if (!entityRef) {
      return {
        valid: false,
        error: 'Entity reference is required to update an entity.'
      }
    }

    const entity = resolveSchema(entityRef, workflow)

    if (!entity) {
      return {
        valid: false,
        error: `Entity "${refDisplayName(entityRef)}" not found.`
      }
    }

    if (name && name.trim() === '') {
      return {
        valid: false,
        error: 'Entity name cannot be empty.'
      }
    }

    if (addFields && Array.isArray(addFields)) {
      for (const field of addFields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All new fields must have a name.'
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }

    if (updateFields && Array.isArray(updateFields)) {
      for (const { fieldName, updates } of updateFields) {
        if (!fieldName) {
          return {
            valid: false,
            error: 'Field name is required for updating a field.'
          }
        }

        if (updates?.relatedEntity) {
          const relatedEntity = resolveSchema(updates.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(updates.relatedEntity)}" not found for field "${fieldName}".`
            }
          }
        }
      }
    }

    if (removeFields && Array.isArray(removeFields)) {
      for (const fieldName of removeFields) {
        if (!fieldName || fieldName.trim() === '') {
          return {
            valid: false,
            error: 'Field name is required for removing a field.'
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating commands (bulk)
 */
const validateCreateCommands = (args, context) => {
  const { commands } = args
  const { workflow } = context

  if (!Array.isArray(commands) || commands.length === 0) {
    return {
      valid: false,
      error: 'At least one command is required.'
    }
  }

  for (const command of commands) {
    const { domainEvent: eventRef, name, fields } = command || {}

    if (!eventRef) {
      return {
        valid: false,
        error: 'Domain event is required to create a command.'
      }
    }

    const event = resolveDomainEvent(eventRef, workflow)

    if (!event) {
      return {
        valid: false,
        error: `Event "${refDisplayName(eventRef)}" not found.`
      }
    }

    if (!name || name.trim() === '') {
      return {
        valid: false,
        error: 'Command name is required.'
      }
    }

    if (fields && Array.isArray(fields)) {
      for (const field of fields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All command fields must have a name.'
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating commands (bulk)
 */
const validateUpdateCommands = (args, context) => {
  const { commands } = args
  const { workflow, cardTypes } = context

  if (!Array.isArray(commands) || commands.length === 0) {
    return {
      valid: false,
      error: 'At least one command update is required.'
    }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))

  for (const update of commands) {
    const { command: commandRef, addFields, updateFields, removeFields } = update || {}

    if (!commandRef) {
      return {
        valid: false,
        error: 'Command reference is required to update a command.'
      }
    }

    const command = resolveSchema(commandRef, workflow, cardTypesMap)

    if (!command) {
      return {
        valid: false,
        error: `Command "${refDisplayName(commandRef)}" not found.`
      }
    }

    if (addFields && Array.isArray(addFields)) {
      for (const field of addFields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All new fields must have a name.'
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }

    if (updateFields && Array.isArray(updateFields)) {
      for (const { fieldName, updates } of updateFields) {
        if (!fieldName) {
          return {
            valid: false,
            error: 'Field name is required for updating a field.'
          }
        }

        if (updates?.relatedEntity) {
          const relatedEntity = resolveSchema(updates.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(updates.relatedEntity)}" not found for field "${fieldName}".`
            }
          }
        }
      }
    }

    if (removeFields && Array.isArray(removeFields)) {
      for (const fieldName of removeFields) {
        if (!fieldName || fieldName.trim() === '') {
          return {
            valid: false,
            error: 'Field name is required for removing a field.'
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating read models (bulk)
 */
const validateCreateReadModels = (args, context) => {
  const { readModels } = args
  const { workflow } = context

  if (!Array.isArray(readModels) || readModels.length === 0) {
    return {
      valid: false,
      error: 'At least one read model is required.'
    }
  }

  for (const readModel of readModels) {
    const { domainEvent: eventRef, name, fields, entity: entityRef } = readModel || {}

    if (!eventRef) {
      return {
        valid: false,
        error: 'Domain event is required to create a read model.'
      }
    }

    const event = resolveDomainEvent(eventRef, workflow)

    if (!event) {
      return {
        valid: false,
        error: `Event "${refDisplayName(eventRef)}" not found.`
      }
    }

    if (!name || name.trim() === '') {
      return {
        valid: false,
        error: 'Read model name is required.'
      }
    }

    if (fields && Array.isArray(fields)) {
      for (const field of fields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All read model fields must have a name.'
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }

    if (entityRef) {
      const entity = resolveSchema(entityRef, workflow)

      if (!entity) {
        return {
          valid: false,
          error: `Entity "${refDisplayName(entityRef)}" not found.`
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating read models (bulk)
 */
const validateUpdateReadModels = (args, context) => {
  const { readModels } = args
  const { workflow, cardTypes } = context

  if (!Array.isArray(readModels) || readModels.length === 0) {
    return {
      valid: false,
      error: 'At least one read model update is required.'
    }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))

  for (const update of readModels) {
    const { readModel: readModelRef, entity: entityRef, addFields, updateFields, removeFields } = update || {}

    if (!readModelRef) {
      return {
        valid: false,
        error: 'Read model reference is required to update a read model.'
      }
    }

    const readModel = resolveSchema(readModelRef, workflow, cardTypesMap)

    if (!readModel) {
      return {
        valid: false,
        error: `Read model "${refDisplayName(readModelRef)}" not found.`
      }
    }

    if (entityRef) {
      const entity = resolveSchema(entityRef, workflow)

      if (!entity) {
        return {
          valid: false,
          error: `Entity "${refDisplayName(entityRef)}" not found.`
        }
      }
    }

    if (addFields && Array.isArray(addFields)) {
      for (const field of addFields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All new fields must have a name.'
          }
        }
      }
    }

    if (updateFields && Array.isArray(updateFields)) {
      for (const { fieldName } of updateFields) {
        if (!fieldName) {
          return {
            valid: false,
            error: 'Field name is required for updating a field.'
          }
        }
      }
    }

    if (removeFields && Array.isArray(removeFields)) {
      for (const fieldName of removeFields) {
        if (!fieldName || fieldName.trim() === '') {
          return {
            valid: false,
            error: 'Field name is required for removing a field.'
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates creating domain event schemas (bulk)
 */
const validateCreateDomainEventSchemas = (args, context) => {
  const { domainEventSchemas } = args
  const { workflow } = context

  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return {
      valid: false,
      error: 'At least one domain event schema is required.'
    }
  }

  for (const domainEventSchema of domainEventSchemas) {
    const { domainEvent: eventRef, name, fields, entity: entityRef } = domainEventSchema || {}

    if (!eventRef) {
      return {
        valid: false,
        error: 'Domain event is required to create a domain event schema.'
      }
    }

    const event = resolveDomainEvent(eventRef, workflow)

    if (!event) {
      return {
        valid: false,
        error: `Event "${refDisplayName(eventRef)}" not found.`
      }
    }

    if (!name || name.trim() === '') {
      return {
        valid: false,
        error: 'Domain event schema name is required.'
      }
    }

    if (fields && Array.isArray(fields)) {
      for (const field of fields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All domain event schema fields must have a name.'
          }
        }

        if (field.relatedEntity) {
          const relatedEntity = resolveSchema(field.relatedEntity, workflow)

          if (!relatedEntity) {
            return {
              valid: false,
              error: `Related entity "${refDisplayName(field.relatedEntity)}" not found for field "${field.name}".`
            }
          }
        }
      }
    }

    if (entityRef) {
      const entity = resolveSchema(entityRef, workflow)

      if (!entity) {
        return {
          valid: false,
          error: `Entity "${refDisplayName(entityRef)}" not found.`
        }
      }
    }
  }

  return { valid: true, error: null }
}

/**
 * Validates updating domain event schemas (bulk)
 */
const validateUpdateDomainEventSchemas = (args, context) => {
  const { domainEventSchemas } = args
  const { workflow, cardTypes } = context

  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return {
      valid: false,
      error: 'At least one domain event schema update is required.'
    }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))

  for (const update of domainEventSchemas) {
    const { domainEventSchema: schemaRef, entity: entityRef, addFields, updateFields, removeFields } = update || {}

    if (!schemaRef) {
      return {
        valid: false,
        error: 'Domain event schema reference is required to update a domain event schema.'
      }
    }

    const schema = resolveSchema(schemaRef, workflow, cardTypesMap)

    if (!schema) {
      return {
        valid: false,
        error: `Domain event schema "${refDisplayName(schemaRef)}" not found.`
      }
    }

    if (entityRef) {
      const entity = resolveSchema(entityRef, workflow)

      if (!entity) {
        return {
          valid: false,
          error: `Entity "${refDisplayName(entityRef)}" not found.`
        }
      }
    }

    if (addFields && Array.isArray(addFields)) {
      for (const field of addFields) {
        if (!field.name || field.name.trim() === '') {
          return {
            valid: false,
            error: 'All new fields must have a name.'
          }
        }
      }
    }

    if (updateFields && Array.isArray(updateFields)) {
      for (const { fieldName } of updateFields) {
        if (!fieldName) {
          return {
            valid: false,
            error: 'Field name is required for updating a field.'
          }
        }
      }
    }

    if (removeFields && Array.isArray(removeFields)) {
      for (const fieldName of removeFields) {
        if (!fieldName || fieldName.trim() === '') {
          return {
            valid: false,
            error: 'Field name is required for removing a field.'
          }
        }
      }
    }
  }

  return { valid: true, error: null }
}

const refDisplayName = (ref) => {
  if (!ref || typeof ref !== 'string') {
    return ref
  }
  const lastSegment = ref.split('/').pop()

  return lastSegment.replace(/([a-z])([A-Z])/g, '$1 $2')
}
